import _ from 'underscore';
import URI from 'urijs';

let lastHash;

const specificDigitsParams = {
  accountId: 'aid',
  customerId: 'cid',
  id: 'id',
  userId: 'uid',
};

const specificDigitsParamsValues = _.values(specificDigitsParams);

const isDigits = s => /^\d+$/.test(s);

const mapSpecificsParams = params => _.mapObject(params, (value, key) => {
  if (_.contains(specificDigitsParamsValues, key) && _.isArray(value)) {
    // In case of an array of params values is returns,
    // we return the 1st digit value or undefined if none
    return _.find(value, isDigits);
  }
  return value;
});

/**
 * This class is a wrapper of HTML5 History API and stores no data itself
 */
export class Router {
  /**
   * An alias of window.history.back();
   * @returns {undefined} - perform window.history.back()
   */
  static back() {
    window.history.back();
  }

  /**
   * An alias of window.history.forward();
   * @returns {undefined} - perform window.history.forward()
   */
  static forward() {
    window.history.forward();
  }

  /**
   * A wrapper of window.history.go();
   * @param {interger|string} numberOrUrl - (required) The parameter can either be a number which
   *                                                   goes to the URL within the specific
   *                                                   position (-1 goes back one page, 1 goes
   *                                                   forward one page),or a string.The string
   *                                                   must be a partial or full URL,and the
   *                                                   function will go to the first URL that
   *                                                   matches the string.
   * @returns {undefined} - perform window.history.go()
   */
  static go(numberOrUrl) {
    window.history.go(numberOrUrl);
  }

  /**
   * Convert route state to URL hash
   * @param {object} state - (required) the route state need to be converted
   *
   * @returns {string} the URL hash string.
   */
  static formatHash(state) {
    const generateHashForEachLevel = (level) => {
      const paramsString = _.isEmpty(_.compact(level.parameters)) ? '' :
        `(${_.map(level.parameters, encodeURIComponent).join(',')})`;

      return `/${encodeURIComponent(level.name)}${paramsString}`;
    };

    const path = _.isEmpty(state.levels) ? '/' :
      _.map(state.levels, generateHashForEachLevel).join('');

    const query = _.isEmpty(state.properties) ? '' :
      `?${URI.buildQuery(state.properties, false, false)}`;

    return `#${path}${query}`;
  }

  /**
   * Convert URL hash to route state
   * @param {string} hash - (required) the hash need to be converted
   *
   * @returns {object} the route state
   */
  static parseHash(hash) {
    const [, path, query] = /^#([^?]+)(?:\?([^?]*))?$/.exec(hash);
    const levels = _.chain((path || '').split('/'))
      .reject(_.isEmpty)
      .map((level) => {
        const res = /^([^(]+)(?:\(([^)]*)\))?$/.exec(level);
        return {
          name: decodeURIComponent(res[1]),
          parameters: _.map(
            decodeURIComponent(res[2] || '').split(','),
            param => param.trim()
          ),
        };
      }).value();

    const properties = URI.parseQuery(query);

    return {
      levels,
      properties,
    };
  }

  /**
   * Convert URL path and query to route state
   * @param {string} url - (required) the url need to be converted
   *
   * @returns {object} the route state
   */
  static parsePathAndQuery(url) {
    const uri = new URI(url);
    const params = URI.parseQuery(uri.query());
    const levels = _.map(uri.segment(), level =>
      _.object(['name', 'parameters'], [decodeURIComponent(level), null]));

    return {
      levels,
      properties: mapSpecificsParams(params),
    };
  }

  /**
   * Perform a navigation based on the new route state
   * @param {object|string} route - (required) The parameter can either be a route state
   *                                           or a URL hash string
   * @returns {undefined} - perform URL change
   */
  static navigate(route) {
    if (_.isObject(route)) {
      window.location.hash = this.formatHash(route);
    } else {
      window.location.href = route;
    }
  }

  /**
   * Calls the callback with a route state object on navigation
   * @param {function} callback - (required) The callback function for navigation
   * @returns {undefined} - listen to event and perform callback
   */
  static onNavigate(callback) {
    const navigationHandler = () => {
      if (lastHash !== window.location.hash) {
        lastHash = window.location.hash;
        callback(this.parseHash(lastHash));
      }
    };

    // Polyfill for IE 11
    window.addEventListener('popstate', navigationHandler);
    window.addEventListener('hashchange', navigationHandler);
  }
}
